'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { put } from '@nineeng/request';
import { PERMISSIONS, LanguageCode } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { getVerifyPermission, constructQuery, mergeCompanyOverGroup } from '@repo/shared/helpers';
import { DetailsTab } from '@repo/components';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroup, getGroupForm } from 'src/api/admin/groups';

// If addressLine1 is locked by companyData, lock addressLine2 too (same registered address block)
function getReadOnlyFields(companyData: Record<string, unknown> | undefined | null): string[] {
	const fields = Object.keys(companyData || {}).filter(
		(key) => companyData?.[key] != null && companyData?.[key] !== '',
	);
	if (fields.includes('addressLine1') && !fields.includes('addressLine2')) {
		fields.push('addressLine2');
	}
	return fields;
}

export const Route = createFileRoute('/_admin/groups/$groupId/details')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			language: (search.language as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const group = await getGroup(params.groupId);
		const form = group?.type?.Form?.slug ? await getGroupForm(group.type.Form.slug, deps.language) : undefined;
		return { group, form };
	},
	component: DetailsPage,
});

function DetailsPage() {
	const { groupId } = Route.useParams();
	const { language: searchLanguage } = Route.useSearch();
	const queryClient = useQueryClient();
	const { group: initialGroup, form: initialForm } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const { data: group } = useQuery({
		queryKey: ['group', groupId],
		queryFn: () => getGroup(groupId),
		initialData: initialGroup,
	});

	const { data: form } = useQuery({
		queryKey: ['group-form', group?.type?.Form?.slug, searchLanguage],
		queryFn: () => (group?.type?.Form?.slug ? getGroupForm(group.type.Form.slug, searchLanguage) : undefined),
		initialData: initialForm,
		enabled: !!group?.type?.Form?.slug,
	});

	const revalidateGroupDetail = () => {
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['groups'] });
	};

	const handleSave = async (groupData: Record<string, any>) => {
		await put(`/api/groups/${groupId}/details`, { ...groupData })
			.then(() =>
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Changes saved',
				}),
			)
			.catch(() => EventEmitter.emit('toast', { type: 'error', message: 'Error' }));
		revalidateGroupDetail();
	};

	const availableLanguages = form?.availableLanguages ?? [];
	const defaultLanguage = form?.language ?? 'en';
	const languages = [...new Set([...availableLanguages, defaultLanguage])] as LanguageCode[];
	const language = languages.find((lang) => lang === searchLanguage) || 'en';

	const handleLanguageChange = (_language: string) => {
		// Note: In TanStack Router, updating search params should be done via navigate
		// For simplicity, we'll reload with the new language
		window.location.search = `?language=${_language}`;
	};

	if (!group) {
		return null;
	}

	return (
		<DetailsTab
			data={mergeCompanyOverGroup(group?.groupData, group?.companyData)}
			form={form}
			onSave={handleSave}
			readOnly={!verifyPermission(PERMISSIONS.adminGroupsEdit)}
			readOnlyFields={getReadOnlyFields(group?.companyData)}
			language={language}
			onLanguageChange={handleLanguageChange}
			availableLanguages={languages}
		/>
	);
}
